import { useEffect, useState } from 'react';

interface UseZoneScrollTrackingOptions {
  enabled?: boolean;
  targetRef: React.RefObject<HTMLElement | null>;
}

/**
 * Hook to track when user enters content area for determining when to show hash navigation links
 */
export function useZoneScrollTracking({
  targetRef,
  enabled = true,
}: UseZoneScrollTrackingOptions) {
  const [hasEnteredGrid, setHasEnteredGrid] = useState(false);

  // Track when user's viewport is positioned at the grid area (scroll-based detection)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!targetRef.current) {
      return;
    }

    // Calculate threshold once and cache it (only recalculated on re-mount)
    const calculateThreshold = () => {
      if (!targetRef.current) {
        return 0;
      }

      const gridRect = targetRef.current.getBoundingClientRect();
      const gridTopAbsolute = gridRect.top + window.scrollY;

      return gridTopAbsolute - 260;
    };

    const cachedThreshold = calculateThreshold();
    let lastState = false;
    let rafId: number | null = null;

    // Ultra-optimized scroll handler with RAF and state change detection
    const handleScroll = () => {
      // Prevent multiple RAF calls
      if (rafId) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;

        const scrollTop = window.scrollY;
        const isAtGridArea = scrollTop >= cachedThreshold;

        // Only update React state if the value actually changed
        if (isAtGridArea !== lastState) {
          lastState = isAtGridArea;
          setHasEnteredGrid(isAtGridArea);
        }
      });
    };

    // Check initial position
    handleScroll();

    // Listen for scroll events with passive for optimal performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [targetRef, enabled]);

  return { hasEnteredGrid };
}
